import { useState, useEffect, useCallback } from 'react';
import { tokenManager, TokenData } from '../services/tokenManager';

export interface UseTokenRefreshResult {
  token: string | null;
  isValid: boolean;
  needsRefresh: boolean;
  timeUntilExpiry: number | null;
  refreshToken: () => Promise<boolean>;
  setToken: (tokenData: TokenData) => void;
  clearToken: () => void;
}

/**
 * React hook for managing JWT token refresh
 * Provides automatic token validation and refresh capabilities
 */
export const useTokenRefresh = (initialToken?: string): UseTokenRefreshResult => {
  const [token, setTokenState] = useState<string | null>(
    initialToken || tokenManager.getCurrentToken()
  );
  const [isValid, setIsValid] = useState<boolean>(false);
  const [needsRefresh, setNeedsRefresh] = useState<boolean>(false);
  const [timeUntilExpiry, setTimeUntilExpiry] = useState<number | null>(null);

  // Update state based on token manager
  const updateState = useCallback(() => {
    const currentToken = tokenManager.getCurrentToken();
    const valid = tokenManager.isTokenValid();
    const refresh = tokenManager.needsRefresh();
    const timeLeft = tokenManager.getTimeUntilExpiry();

    setTokenState(currentToken);
    setIsValid(valid);
    setNeedsRefresh(refresh);
    setTimeUntilExpiry(timeLeft);
  }, []);

  // Initialize token manager if we have an initial token
  useEffect(() => {
    if (initialToken && !tokenManager.getCurrentToken()) {
      tokenManager.setToken({
        token: initialToken,
        expiresAt: new Date(Date.now() + 3600000).toISOString(), // Default 1 hour
        expiresIn: 3600
      });
    }
    updateState();
  }, [initialToken, updateState]);

  // Set up periodic state updates
  useEffect(() => {
    const interval = setInterval(updateState, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, [updateState]);

  // Refresh token function
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const result = await tokenManager.refreshToken();
      updateState();
      return result.success;
    } catch (error) {
      console.error('Token refresh failed:', error);
      updateState();
      return false;
    }
  }, [updateState]);

  // Set token function
  const setToken = useCallback((tokenData: TokenData) => {
    tokenManager.setToken(tokenData);
    updateState();
  }, [updateState]);

  // Clear token function
  const clearToken = useCallback(() => {
    tokenManager.clearToken();
    updateState();
  }, [updateState]);

  return {
    token,
    isValid,
    needsRefresh,
    timeUntilExpiry,
    refreshToken,
    setToken,
    clearToken
  };
};
